import React, { FC, useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Text,
  CardHeader,
  RadioButton,
  RadioGroup,
  Checkbox,
  Link,
  Input,
} from '@amzn/storm-ui';
import { Form } from '@amzn/storm-ui-column-form-kit';
import { MessageBundle } from '@amzn/arb-tools';
import InputController from '../../../shared/components/form/InputController';
import DropdownController from '../../../shared/components/form/DropdownController';
import ReadOnlyFormField from '../../../shared/components/form/ReadOnlyFormRow';
import { CustomFormRow } from '../../../shared/components/form/style';
import { RegistrationFormCard } from '../../../shared/features/account-registration/style';
import {
  PHONE_VALIDATION,
  WEBSITE_VALIDATION,
} from '../../../shared/components/form/constants';
import { EMAIL_VALIDATION_REGEX } from '../../../shared/constants';
import {
  optionalVatValidator,
  VAT_NOT_APPLICABLE_COUNTRIES,
  VAT_REQUIRED_COUNTRIES,
} from '../../dspActivationRequest/constants';
import { ActivatePageContext } from '../../../shared/services/activation/activation';
import {
  BILLING_HEADER_KEY,
  ACCOUNT_MANAGER_ID_LABEL_KEY,
  OWNER_LABEL_KEY,
  OWNER_DESCRIPTION_KEY,
  AGENCY_LABEL_KEY,
  AGENCY_DESCRIPTION_KEY,
  COMPANY_NAME_LABEL_KEY,
  CONTACT_NAME_LABEL_KEY,
  PRIMARY_EMAIL_LABEL_KEY,
  PHONE_NUMBER_LABEL_KEY,
  WEBSITE_LABEL_KEY,
  BUSINESS_REG_NUMBER_LABEL_KEY,
  ADDRESS_LINE_1_LABEL_KEY,
  ADDRESS_LINE_2_LABEL_KEY,
  POSTAL_CODE_LABEL_KEY,
  CITY_LABEL_KEY,
  COUNTRY_LABEL_KEY,
  STATE_LABEL_KEY,
  VAT_LABEL_KEY,
  VAT_CHECKBOX_LABEL_KEY,
  VAT_AGREEMENT_LINK_KEY,
  VAT_ERROR_KEY,
  VAT_NOT_APPLICABLE_ERROR_KEY,
  TR_TAX_OFFICE_LABEL_KEY,
  PAYMENT_TERMS_LABEL_KEY,
  PAYMENT_30_DAYS_KEY,
  PAYMENT_45_DAYS_KEY,
  PAYMENT_60_DAYS_KEY,
  OPTIONAL_LABEL_KEY,
  SELECT_PLACEHOLDER_KEY,
  REQUIRED_ERROR_KEY,
  MAX_LENGTH_50_ERROR_KEY,
  MAX_LENGTH_60_ERROR_KEY,
  MAX_LENGTH_100_ERROR_KEY,
  EMAIL_ERROR_KEY,
  PHONE_ERROR_KEY,
  WEBSITE_ERROR_KEY,
} from '../constants';

interface ActivateBillingProps {
  intl: MessageBundle;
  context: ActivatePageContext;
}

const ActivateBilling: FC<ActivateBillingProps> = ({ intl, context }) => {
  const { watch, setValue, control, trigger } = useFormContext();
  const selectedCountry = watch('billingCountry') as string;
  const select = intl.getMessage(SELECT_PLACEHOLDER_KEY);
  const required = intl.getMessage(REQUIRED_ERROR_KEY);
  const optional = intl.getMessage(OPTIONAL_LABEL_KEY);
  const maxLen50 = intl.getMessage(MAX_LENGTH_50_ERROR_KEY);
  const maxLen60 = intl.getMessage(MAX_LENGTH_60_ERROR_KEY);
  const maxLen100 = intl.getMessage(MAX_LENGTH_100_ERROR_KEY);
  const emailError = intl.getMessage(EMAIL_ERROR_KEY);
  const phoneError = intl.getMessage(PHONE_ERROR_KEY);
  const websiteError = intl.getMessage(WEBSITE_ERROR_KEY);
  const vatError = intl.getMessage(VAT_ERROR_KEY);
  const vatNotApplicableError = intl.getMessage(VAT_NOT_APPLICABLE_ERROR_KEY);
  const vatRequired = VAT_REQUIRED_COUNTRIES.includes(selectedCountry);

  const billingCountryItems = useMemo(
    () =>
      (context.billingCountries || []).map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    [context.billingCountries],
  );

  const billingCountry = (context.billingCountries || []).find(
    (c) => c.isoCode === selectedCountry,
  );
  const stateItems = useMemo(
    () =>
      (billingCountry?.states || []).map((s) => ({
        value: s.name,
        label: s.name,
      })),
    [billingCountry?.states],
  );
  const hasStates = stateItems.length > 0;

  const handleCountryChange = (value: string) => {
    setValue('state', '');
    setValue('billingCountry', value);
  };

  const paymentTermsItems = [
    { value: 'DAYS_30', label: intl.getMessage(PAYMENT_30_DAYS_KEY) },
    { value: 'DAYS_45', label: intl.getMessage(PAYMENT_45_DAYS_KEY) },
    { value: 'DAYS_60', label: intl.getMessage(PAYMENT_60_DAYS_KEY) },
  ];

  return (
    <RegistrationFormCard>
      <CardHeader padding="small">
        <Text size="xlarge">{intl.getMessage(BILLING_HEADER_KEY)}</Text>
      </CardHeader>
      <Form id="billing">
        {/* Account manager type — read-only radio */}
        <ReadOnlyFormField
          label={intl.getMessage(ACCOUNT_MANAGER_ID_LABEL_KEY)}
          id="account-manager-type-display"
        >
          <RadioGroup
            hideLabel
            label=""
            name="account-manager-type-display"
            disabled
            selectedValue={context.managerAccountType}
          >
            <div>
              <RadioButton
                id="account-manager-type-display-Owner"
                value="Owner"
                label={intl.getMessage(OWNER_LABEL_KEY)}
              />
              <Text color="secondary" size="xsmall">
                {intl.getMessage(OWNER_DESCRIPTION_KEY)}
              </Text>
            </div>
            <div>
              <RadioButton
                id="account-manager-type-display-Agency"
                value="Agency"
                label={intl.getMessage(AGENCY_LABEL_KEY)}
              />
              <Text color="secondary" size="xsmall">
                {intl.getMessage(AGENCY_DESCRIPTION_KEY)}
              </Text>
            </div>
          </RadioGroup>
        </ReadOnlyFormField>
        <InputController
          label={intl.getMessage(COMPANY_NAME_LABEL_KEY)}
          id="company-name-input"
          controllerName="companyName"
          registerOptions={{
            required,
            maxLength: { value: 50, message: maxLen50 },
          }}
        />
        <InputController
          label={intl.getMessage(CONTACT_NAME_LABEL_KEY)}
          id="contact-name-input"
          controllerName="contactName"
          registerOptions={{
            required,
            maxLength: { value: 50, message: maxLen50 },
          }}
        />
        <InputController
          label={intl.getMessage(PRIMARY_EMAIL_LABEL_KEY)}
          id="contact-email"
          controllerName="contactEmail"
          registerOptions={{
            required,
            pattern: { value: EMAIL_VALIDATION_REGEX, message: emailError },
          }}
        />
        <InputController
          label={intl.getMessage(PHONE_NUMBER_LABEL_KEY)}
          id="contact-phone-number"
          controllerName="phoneNumber"
          registerOptions={{
            required,
            pattern: { value: PHONE_VALIDATION, message: phoneError },
          }}
        />
        <InputController
          label={intl.getMessage(WEBSITE_LABEL_KEY)}
          id="website-input"
          controllerName="website"
          registerOptions={{
            required,
            pattern: { value: WEBSITE_VALIDATION, message: websiteError },
          }}
        />
        <InputController
          label={intl.getMessage(BUSINESS_REG_NUMBER_LABEL_KEY)}
          id="business-registration-number-input"
          controllerName="businessRegistrationNumber"
          optionalTag={optional}
        />
        <InputController
          label={intl.getMessage(ADDRESS_LINE_1_LABEL_KEY)}
          id="address-line-1"
          controllerName="addressLine1"
          registerOptions={{
            required,
            maxLength: { value: 100, message: maxLen100 },
          }}
        />
        <InputController
          label={intl.getMessage(ADDRESS_LINE_2_LABEL_KEY)}
          id="address-line-2"
          controllerName="addressLine2"
          optionalTag={optional}
          registerOptions={{ maxLength: { value: 60, message: maxLen60 } }}
        />
        <InputController
          label={intl.getMessage(POSTAL_CODE_LABEL_KEY)}
          id="address-postalCode"
          controllerName="postalCode"
          registerOptions={{ required }}
        />
        <InputController
          label={intl.getMessage(CITY_LABEL_KEY)}
          id="address-city"
          controllerName="city"
          registerOptions={{ required }}
        />
        <DropdownController
          label={intl.getMessage(COUNTRY_LABEL_KEY)}
          overrideDropdownLabel={select}
          id="address-country-isoCode"
          controllerName="billingCountry"
          items={billingCountryItems}
          registerOptions={{ required }}
          callback={handleCountryChange}
          disabled
        />
        {hasStates ? (
          <DropdownController
            label={intl.getMessage(STATE_LABEL_KEY)}
            overrideDropdownLabel={select}
            id="address-state-dropdown"
            controllerName="state"
            items={stateItems}
            registerOptions={{ required }}
          />
        ) : (
          <InputController
            label={intl.getMessage(STATE_LABEL_KEY)}
            id="address-state-input"
            controllerName="state"
            registerOptions={{ required }}
          />
        )}
        <CustomFormRow
          label={intl.getMessage(VAT_LABEL_KEY)}
          optionalTag={vatRequired ? undefined : optional}
          id="vat-input"
          controlComponents={[
            (componentId: string) => (
              <Controller
                name="vatRegistrationNumber"
                control={control}
                rules={{
                  ...(vatRequired ? { required } : {}),
                  validate: VAT_NOT_APPLICABLE_COUNTRIES.includes(
                    selectedCountry,
                  )
                    ? (value: string) => !value || vatNotApplicableError
                    : optionalVatValidator(selectedCountry, vatError),
                }}
                render={({ field, fieldState }) => (
                  <Input
                    name={field.name}
                    statusType={fieldState.error ? 'error' : undefined}
                    message={fieldState.error?.message}
                    id={componentId}
                    fullWidth
                    onChange={(event) => {
                      field.onChange(event.target.value);
                      trigger('vatRegistrationNumber');
                    }}
                    type="text"
                    value={field.value}
                  />
                )}
              />
            ),
            () => (
              <Checkbox
                label={
                  <span>
                    {intl.getMessage(VAT_CHECKBOX_LABEL_KEY)}
                    <Link
                      href="/dsp/agreement/taxIdAgreement/en"
                      target="_blank"
                    >
                      {intl.getMessage(VAT_AGREEMENT_LINK_KEY)}
                    </Link>
                  </span>
                }
                checked={watch('vatAgreementAccepted')}
                onChange={(e) =>
                  setValue(
                    'vatAgreementAccepted',
                    (e.target as HTMLInputElement).checked,
                  )
                }
              />
            ),
          ]}
        />
        {selectedCountry === 'TR' && (
          <InputController
            label={intl.getMessage(TR_TAX_OFFICE_LABEL_KEY)}
            id="tr-tax-office-input"
            controllerName="trTaxOffice"
            registerOptions={{ required }}
          />
        )}
        <DropdownController
          label={intl.getMessage(PAYMENT_TERMS_LABEL_KEY)}
          overrideDropdownLabel={select}
          id="payment-terms"
          controllerName="paymentTerms"
          items={paymentTermsItems}
          registerOptions={{ required }}
        />
      </Form>
    </RegistrationFormCard>
  );
};

export default ActivateBilling;
